import { toAdMedia } from "src/pages/ad-studio/insights/performance/utils/rows";
import {
  fatigueBadge,
  memberLabel,
  metricOf,
  statusBadge,
} from "src/pages/ad-studio/insights/performance/utils/meta";
import pluralize from "pluralize";
import {
  getImpactTier,
  type ImpactTier,
  impactLabels,
} from "src/pages/ad-studio/insights/shared/fatigue";
import { formatMetricValue } from "src/pages/ad-studio/insights/shared/format";
import {
  getMetricDistributionColor,
  type MetricDistribution,
  type MetricDistributions,
} from "src/pages/ad-studio/insights/performance/utils/metric-distribution";
import {
  shownCreativeOf,
  steppedChoiceOf,
} from "src/pages/ad-studio/insights/ad-creatives";
import type {
  AdMedia,
  CreativeChoice,
  AdRow,
  AdSetRow,
  CampaignRow,
  FatigueRow,
  InsightsFailure,
  InsightsQueryResult,
  MetricCell,
  PilledAdRow,
  EntityStatus,
  GroupBy,
  GroupRow,
  MediaItem,
  VideoSource,
  PerformanceRow,
  MetricColumn,
  MetricKey,
  AdViewMeta,
  Reported,
  CampaignViewMeta,
  InsightsMeta,
  ViewMeta,
} from "src/pages/ad-studio/insights/performance/types";
import type {
  CardItem,
  CardOf,
  CardPill,
  CardState,
  CellBadge,
  CellState,
  ColumnSpec,
  ColumnSpecs,
  ColumnState,
  EntityCardMedia,
  FatigueCellContext,
  FieldColumnSpec,
  Kind,
  ListColumn,
  ListOf,
  ListResult,
  MetricColumnsSpec,
  MetricComparison,
  MetricDelta,
  MetricRowState,
  Order,
  Page,
  PageArgs,
  PageParts,
  PagingState,
  RowOf,
  SortState,
  TabPage,
  TextTone,
  Thumbnail,
} from "src/pages/ad-studio/insights/performance/view/types";

const EMPTY_CELL: CellState = { kind: "text", text: "—", tone: "tertiary" };

const lastPageOf = ({
  total,
  rowsPerPage,
}: {
  total: number;
  rowsPerPage: number;
}): number => Math.max(0, Math.ceil(total / rowsPerPage) - 1);

export const countTextOf = ({
  noun,
  total,
}: {
  noun: string;
  total: number;
}): string => pluralize(noun, total, true);

export function toPagingState({
  page,
  total,
  rowsPerPage,
  noun,
}: {
  page: number;
  total: number;
  rowsPerPage: number;
  noun: string;
}): PagingState {
  if (total === 0) {
    return { kind: "no-rows" };
  }
  return total <= rowsPerPage
    ? { kind: "one-page" }
    : {
        kind: "pages",
        page: Math.min(page, lastPageOf({ total, rowsPerPage })),
        rowsPerPage,
        total,
        noun,
      };
}

const sortStateOf = <Key extends string>({
  order,
  key,
}: {
  order: Order<Key>;
  key: Key;
}): SortState => {
  if (order.key !== key) {
    return "unsorted";
  }
  return order.direction === "asc" ? "ascending" : "descending";
};

const metricColumns = ({
  metrics,
  order,
}: {
  metrics: readonly MetricColumn[];
  order: Order<MetricKey>;
}): MetricColumnsSpec => ({
  kind: "metrics",
  headers: metrics.map((metric) => ({
    metric,
    sort: {
      kind: "sortable",
      key: metric.key,
      state: sortStateOf({ order, key: metric.key }),
    },
  })),
});

const toMetricHeader = ({
  metric,
  sort,
}: MetricColumnsSpec["headers"][number]): ColumnState => ({
  id: metric.key,
  label: metric.label,
  info: { kind: "no-info" },
  width: "metric",
  pinned: "scrolls",
  sort,
});

const shownCells = ({
  metrics,
  cells,
}: {
  metrics: readonly MetricColumn[];
  cells: readonly MetricCell[];
}): readonly MetricCell[] => {
  const shown = new Set(metrics.map((column) => column.key));
  return cells.filter((cell) => shown.has(cell.column.key));
};

const videoThumbnail = (video: VideoSource): Thumbnail => {
  switch (video.kind) {
    case "file": {
      return { kind: "video", url: video.url };
    }
    case "youtube": {
      return { kind: "image", url: video.thumbnailUrl };
    }
    default: {
      return video satisfies never;
    }
  }
};

export const toThumbnail = (item: MediaItem): Thumbnail => {
  switch (item.kind) {
    case "image": {
      return { kind: "image", url: item.url };
    }
    case "video": {
      return videoThumbnail(item.video);
    }
    default: {
      return item satisfies never;
    }
  }
};

const creativesText = (row: AdRow): readonly string[] =>
  row.creativeCount === 0
    ? []
    : [pluralize("creative", row.creativeCount, true)];

const groupNameOf = (row: GroupRow): string => {
  switch (row.name.kind) {
    case "ad": {
      return row.previews[0].entityName;
    }
    case "attribute": {
      return row.name.label;
    }
    default: {
      return row.name satisfies never;
    }
  }
};

const groupNameCell = (
  row: GroupRow,
): Extract<CellState, { kind: "entity-name" | "previewed-name" }> => {
  const name = groupNameOf(row);
  const { media } = row.previews[0];
  switch (media.kind) {
    case "media": {
      return {
        kind: "previewed-name",
        name,
        preview: toThumbnail(media.creatives[0].item),
        details: [],
      };
    }
    case "no-media": {
      return { kind: "entity-name", name, details: [] };
    }
    default: {
      return media satisfies never;
    }
  }
};

const adNameCell = (row: AdRow): CellState => {
  const details = creativesText(row);
  switch (row.media.kind) {
    case "media": {
      return {
        kind: "previewed-name",
        name: row.entityName,
        preview: toThumbnail(row.media.creatives[0].item),
        details,
      };
    }
    case "no-media": {
      return { kind: "entity-name", name: row.entityName, details };
    }
    default: {
      return row.media satisfies never;
    }
  }
};

const nameCell = (row: PerformanceRow): CellState => {
  switch (row.entityType) {
    case "ad": {
      return adNameCell(row);
    }
    case "ad_set":
    case "campaign": {
      return { kind: "entity-name", name: row.entityName, details: [] };
    }
    default: {
      return row satisfies never;
    }
  }
};

const objectiveCell = (row: CampaignRow): CellState => {
  switch (row.objective.kind) {
    case "objective": {
      return { kind: "text", text: row.objective.text, tone: "secondary" };
    }
    case "no-objective": {
      return EMPTY_CELL;
    }
    default: {
      return row.objective satisfies never;
    }
  }
};

const statusCell = ({
  status,
  view,
}: {
  status: EntityStatus;
  view: ViewMeta;
}): CellState => {
  switch (status.kind) {
    case "status": {
      return {
        kind: "badges",
        badges: [statusBadge({ view, value: status.value })],
      };
    }
    case "no-status": {
      return EMPTY_CELL;
    }
    default: {
      return status satisfies never;
    }
  }
};

const healthCell = ({
  row,
  view,
}: {
  row: AdRow;
  view: AdViewMeta;
}): CellState =>
  row.fatigue.kind === "no-fatigue-row"
    ? EMPTY_CELL
    : {
        kind: "badges",
        badges: [fatigueBadge({ view, value: row.fatigue.status })],
      };

const nameColumn = (label: string): FieldColumnSpec<PerformanceRow> => ({
  kind: "field",
  column: {
    id: "name",
    label,
    info: { kind: "no-info" },
    width: "name",
    pinned: "scrolls",
  },
  sort: { kind: "not-sortable" },
  cell: nameCell,
});

const objectiveColumn = (
  view: CampaignViewMeta,
): FieldColumnSpec<CampaignRow> => ({
  kind: "field",
  column: {
    id: "objective",
    label: memberLabel(view, view.layouts.table.objective),
    info: { kind: "no-info" },
    width: "short-text",
    pinned: "scrolls",
  },
  sort: { kind: "not-sortable" },
  cell: objectiveCell,
});

const healthColumn = (view: AdViewMeta): FieldColumnSpec<AdRow> => {
  return {
    kind: "field",
    column: {
      id: "health",
      label: memberLabel(view, view.layouts.fatigue.status),
      info: {
        kind: "info",
        text: "Based on CTR decay from peak, week-over-week change, and ad frequency",
      },
      width: "short-text",
      pinned: "scrolls",
    },
    sort: { kind: "not-sortable" },
    cell: (row) => healthCell({ row, view }),
  };
};

const statusColumn = (view: ViewMeta): FieldColumnSpec<PerformanceRow> => ({
  kind: "field",
  column: {
    id: "status",
    label: memberLabel(view, view.layouts.table.status),
    info: { kind: "no-info" },
    width: "badge",
    pinned: "scrolls",
  },
  sort: { kind: "not-sortable" },
  cell: (row) => statusCell({ status: row.status, view }),
});

type EntityColumnSpecs = Pick<ColumnSpecs, "ads" | "ad-sets" | "campaigns">;

function entityColumnSpecs({
  meta,
  metrics,
  order,
  entityLabel,
}: {
  meta: InsightsMeta;
  metrics: readonly MetricColumn[];
  order: Order<MetricKey>;
  entityLabel: string;
}): EntityColumnSpecs {
  const metricSpecs = metricColumns({ metrics, order });
  return {
    ads: [
      nameColumn(entityLabel),
      statusColumn(meta.views.ad),
      metricSpecs,
      healthColumn(meta.views.ad),
    ],
    "ad-sets": [
      nameColumn(entityLabel),
      statusColumn(meta.views.ad_set),
      metricSpecs,
    ],
    campaigns: [
      nameColumn(entityLabel),
      objectiveColumn(meta.views.campaign),
      statusColumn(meta.views.campaign),
      metricSpecs,
    ],
  };
}

const toStatusBadges = ({
  row,
  view,
}: {
  row: GroupRow;
  view: ViewMeta;
}): readonly CellBadge[] =>
  row.statuses.map((value) => statusBadge({ view, value }));

function groupColumns({
  dimension,
  view,
  metrics,
  order,
}: {
  dimension: string;
  view: AdViewMeta;
  metrics: readonly MetricColumn[];
  order: Order<MetricKey>;
}): readonly ColumnSpec<GroupRow>[] {
  const { groupCard } = view.layouts;
  const plain = ({
    id,
    label,
    width,
  }: {
    id: string;
    label: string;
    width: ColumnState["width"];
  }): Omit<ColumnState, "sort"> => ({
    id,
    label,
    info: { kind: "no-info" },
    width,
    pinned: "scrolls",
  });
  return [
    {
      kind: "field",
      column: plain({ id: "name", label: dimension, width: "name" }),
      sort: { kind: "not-sortable" },
      cell: groupNameCell,
    },
    {
      kind: "field",
      column: plain({
        id: "ads",
        label: memberLabel(view, groupCard.count),
        width: "count",
      }),
      sort: { kind: "not-sortable" },
      cell: (row) => ({ kind: "number", text: String(row.adCount) }),
    },
    {
      kind: "field",
      column: plain({
        id: "statuses",
        label: memberLabel(view, groupCard.statuses),
        width: "short-text",
      }),
      sort: { kind: "not-sortable" },
      cell: (row) => ({
        kind: "badges",
        badges: toStatusBadges({ row, view }),
      }),
    },
    {
      kind: "field",
      column: plain({
        id: "objectives",
        label: memberLabel(view, groupCard.objectives),
        width: "short-text",
      }),
      sort: { kind: "not-sortable" },
      cell: (row) =>
        row.objectives.length === 0
          ? EMPTY_CELL
          : {
              kind: "text",
              text: row.objectives.join(", "),
              tone: "secondary",
            },
    },
    metricColumns({ metrics, order }),
  ];
}

export const toColumnStates = (
  specs: readonly ColumnSpec<never>[],
): readonly ColumnState[] =>
  specs.flatMap((spec): readonly ColumnState[] => {
    switch (spec.kind) {
      case "field": {
        return [{ ...spec.column, sort: spec.sort }];
      }
      case "metrics": {
        return spec.headers.map(toMetricHeader);
      }
      default: {
        return spec satisfies never;
      }
    }
  });

type Metered = { metrics: readonly MetricCell[] };

const metricCell = ({
  row,
  metric,
}: {
  row: Metered;
  metric: MetricColumn;
}): CellState => {
  const reported = row.metrics.find((cell) => cell.column.key === metric.key);
  return reported === undefined
    ? EMPTY_CELL
    : {
        kind: "number",
        text: formatMetricValue(reported.value, metric.format),
      };
};

export const toListColumns = <Row extends Metered>(
  specs: readonly ColumnSpec<Row>[],
): readonly ListColumn<Row>[] =>
  specs.flatMap((spec): readonly ListColumn<Row>[] => {
    switch (spec.kind) {
      case "field": {
        return [
          { state: { ...spec.column, sort: spec.sort }, cell: spec.cell },
        ];
      }
      case "metrics": {
        return spec.headers.map((header) => ({
          state: toMetricHeader(header),
          cell: (row: Row) => metricCell({ row, metric: header.metric }),
        }));
      }
      default: {
        return spec satisfies never;
      }
    }
  });

export const toPageParts = <Row extends Metered>({
  specs,
  total,
  args,
}: {
  specs: readonly ColumnSpec<Row>[];
  total: number;
  args: PageArgs;
}): PageParts<Row> => ({
  columns: toListColumns(specs),
  paging: toPagingState({
    page: args.paging.page,
    total,
    rowsPerPage: args.paging.rowsPerPage,
    noun: args.noun,
  }),
  empty: { title: `No ${args.noun} found`, message: args.emptyMessage },
});

const toMetricDelta = ({
  column,
  value,
  distribution,
}: {
  column: MetricColumn;
  value: Reported;
  distribution: MetricDistribution | "no-distribution";
}): MetricDelta => {
  if (value === "not-reported") {
    return { kind: "no-value" };
  }
  if (distribution === "no-distribution" || distribution.average === 0) {
    return { kind: "no-average" };
  }
  const delta = value - distribution.average;
  const percent = (delta / Math.abs(distribution.average)) * 100;
  const better = column.polarity === "lower" ? delta <= 0 : delta >= 0;
  return {
    kind: "versus-average",
    text: `${delta > 0 ? "+" : ""}${percent.toFixed(0)}%`,
    tone: better ? "better" : "worse",
  };
};

const toMetricRows = ({
  metrics,
  cells,
  distributions,
}: {
  metrics: readonly MetricColumn[];
  cells: readonly MetricCell[];
  distributions: MetricDistributions;
}): readonly MetricRowState[] =>
  shownCells({ metrics, cells }).map(({ column, value }) => ({
    key: column.key,
    label: column.label,
    description: column.description,
    value: formatMetricValue(value, column.format),
    comparison: toMetricComparison({ column, value, distributions }),
  }));

const toMetricComparison = ({
  column,
  value,
  distributions,
}: {
  column: MetricColumn;
  value: Reported;
  distributions: MetricDistributions;
}): MetricComparison => {
  switch (distributions.kind) {
    case "average-pending": {
      return { kind: "average-pending" };
    }
    case "distributions": {
      const distribution =
        distributions.byMetric.get(column.key) ?? "no-distribution";
      return {
        kind: "compared",
        tone: getMetricDistributionColor({
          polarity: column.polarity,
          value,
          distribution,
        }),
        delta: toMetricDelta({ column, value, distribution }),
      };
    }
    default: {
      return distributions satisfies never;
    }
  }
};

const statusPill = (badge: CellBadge): CardPill => ({
  kind: "status",
  label: badge.label,
  live: badge.variant === "success",
});

const toStatusPills = ({
  row,
  view,
}: {
  row: PilledAdRow;
  view: ViewMeta;
}): readonly CardPill[] => [
  ...(row.status.kind === "status"
    ? [statusPill(statusBadge({ view, value: row.status.value }))]
    : []),
  ...(row.objective.kind === "objective"
    ? [{ kind: "text" as const, text: row.objective.text }]
    : []),
  ...(row.creativeCount > 1
    ? [{ kind: "text" as const, text: `${row.creativeCount} creatives` }]
    : []),
];

export const toPage = <K extends Kind>({
  list,
  specs,
  args,
}: {
  list: ListOf<K>;
  specs: ColumnSpecs;
  args: PageArgs;
}): Page<K> => {
  const kind: K = list.kind;
  return {
    kind,
    list,
    ...toPageParts({ specs: specs[kind], total: list.total, args }),
  };
};

export const toTabPage = <K extends Kind>({
  list,
  specs,
  cardOf,
  args,
}: {
  list: ListOf<K>;
  specs: ColumnSpecs;
  cardOf: CardOf;
  args: PageArgs;
}): TabPage<K> => {
  const kind: K = list.kind;
  const toCard: CardOf[K] = cardOf[kind];
  return {
    kind,
    list,
    cards: list.rows.map((row: RowOf<K>) => ({ row, card: toCard(row) })),
    ...toPageParts({ specs: specs[kind], total: list.total, args }),
  };
};

type CardMetrics = {
  metrics: readonly MetricColumn[];
  distributions: MetricDistributions;
};

const toAdCard = ({
  row,
  pills,
  measures,
}: {
  row: AdRow;
  pills: readonly CardPill[];
  measures: CardMetrics;
}): CardState => ({
  kind: "ad",
  name: row.entityName,
  media: row.media,
  pills,
  metrics: toMetricRows({ ...measures, cells: row.metrics }),
});

const toParentCard = ({
  row,
  measures,
}: {
  row: AdSetRow | CampaignRow;
  measures: CardMetrics;
}): CardState => ({
  kind: "parent",
  name: row.entityName,
  metrics: toMetricRows({ ...measures, cells: row.metrics }),
});

const representativeCreativeOf = (media: AdMedia) => {
  switch (media.kind) {
    case "media": {
      return [media.creatives[0]];
    }
    case "no-media": {
      return [];
    }
    default: {
      return media satisfies never;
    }
  }
};

const toGroupCard = ({
  row,
  measures,
}: {
  row: GroupRow;
  measures: CardMetrics;
}): CardState => ({
  kind: "group",
  title: groupNameOf(row),
  media: toAdMedia(
    row.previews.flatMap(({ media }) => representativeCreativeOf(media)),
  ),
  pills: [{ kind: "text", text: pluralize("ad", row.adCount, true) }],
  metrics: toMetricRows({ ...measures, cells: row.metrics }),
});

/** The Performance tab's card for each kind of row. */
export const tabCardOf = ({ measures }: { measures: CardMetrics }): CardOf => ({
  ads: (row) => toAdCard({ row, pills: [], measures }),
  "ad-sets": (row) => toParentCard({ row, measures }),
  campaigns: (row) => toParentCard({ row, measures }),
  groups: (row) => toGroupCard({ row, measures }),
});

export const cardNameOf = (card: CardState): string => {
  switch (card.kind) {
    case "ad":
    case "parent": {
      return card.name;
    }
    case "group": {
      return card.title;
    }
    default: {
      return card satisfies never;
    }
  }
};

/** Ad cards with status pills, for surfaces that show only cards. */
export const toPilledAdCards = ({
  rows,
  view,
  measures,
}: {
  rows: readonly PilledAdRow[];
  view: ViewMeta;
  measures: CardMetrics;
}): readonly CardItem<PilledAdRow>[] =>
  rows.map((row) => ({
    row,
    card: toAdCard({ row, pills: toStatusPills({ row, view }), measures }),
  }));

export const toListResult = <Value, Page>({
  result,
  planned,
  toPage,
}: {
  result: InsightsQueryResult<Value, InsightsFailure>;
  planned: readonly ColumnState[];
  toPage: (value: Value) => Page;
}): ListResult<Page> => {
  switch (result.status) {
    case "loading": {
      return { status: "loading", planned };
    }
    case "failed": {
      return result;
    }
    case "loaded": {
      return { status: "loaded", page: toPage(result.value) };
    }
    default: {
      return result satisfies never;
    }
  }
};

export const sortedOrder = <Key extends string>({
  order,
  key,
}: {
  order: Order<Key>;
  key: Key;
}): Order<Key> =>
  order.key === key
    ? { key, direction: order.direction === "asc" ? "desc" : "asc" }
    : { key, direction: "desc" };

export const columnSpecsOf = ({
  label,
  meta,
  metrics,
  order,
}: {
  label: string;
  meta: InsightsMeta;
  metrics: readonly MetricColumn[];
  order: Order<MetricKey>;
}): ColumnSpecs => {
  return {
    ...entityColumnSpecs({ meta, metrics, order, entityLabel: label }),
    groups: groupColumns({
      dimension: label,
      view: meta.views.ad,
      metrics,
      order,
    }),
  };
};

export const plannedColumns = ({
  groupBy,
  specs,
}: {
  groupBy: GroupBy;
  specs: ColumnSpecs;
}): readonly ColumnState[] => {
  switch (groupBy.kind) {
    case "ads": {
      return toColumnStates(specs.ads);
    }
    case "entities": {
      switch (groupBy.grain) {
        case "ad_set": {
          return toColumnStates(specs["ad-sets"]);
        }
        case "campaign": {
          return toColumnStates(specs.campaigns);
        }
        default: {
          return groupBy satisfies never;
        }
      }
    }
    case "attribute": {
      return toColumnStates(specs.groups);
    }
    default: {
      return groupBy satisfies never;
    }
  }
};

const percentText = ({
  value,
  digits,
}: {
  value: Reported;
  digits: number;
}): string => (value === "not-reported" ? "—" : `${value.toFixed(digits)}%`);

const fatigueNumber = ({
  row,
  text,
  tone,
}: {
  row: FatigueRow;
  text: (
    fatigue: Extract<FatigueRow["fatigue"], { kind: "reported" }>,
  ) => string;
  tone: TextTone;
}): CellState =>
  row.fatigue.kind === "no-fatigue-row"
    ? EMPTY_CELL
    : { kind: "text", text: text(row.fatigue), tone };

const wowTone = (change: Reported): TextTone => {
  if (change === "not-reported") {
    return "tertiary";
  }
  if (change < -10) {
    return "danger";
  }
  return change > 10 ? "success" : "secondary";
};

const IMPACT_TONES: Record<ImpactTier, TextTone> = {
  very_high: "danger",
  high: "warning",
  medium: "info",
  low: "secondary",
};

const wowText = (change: Reported): string =>
  change === "not-reported"
    ? "—"
    : `${change > 0 ? "+" : ""}${change.toFixed(0)}%`;

const spendText = (spend: Reported): string => {
  if (spend === "not-reported") {
    return "—";
  }
  return spend >= 1000
    ? `$${(spend / 1000).toFixed(1)}K`
    : `$${Math.round(spend)}`;
};

const impactCell = ({
  value,
  totalSpend,
}: {
  value: Reported;
  totalSpend: FatigueCellContext["totalSpend"];
}): CellState => {
  switch (totalSpend.status) {
    case "loading": {
      return EMPTY_CELL;
    }
    case "failed": {
      return { kind: "text", text: spendText(value), tone: "secondary" };
    }
    case "loaded": {
      const total = totalSpend.value;
      const tier = getImpactTier(
        value === "not-reported" ? 0 : value,
        total === "not-reported" ? 0 : total,
      );
      return {
        kind: "text",
        text: `${impactLabels[tier]} (${spendText(value)})`,
        tone: IMPACT_TONES[tier],
      };
    }
    default: {
      return totalSpend satisfies never;
    }
  }
};

const causeText = ({
  row,
  labels,
}: {
  row: FatigueRow;
  labels: ReadonlyMap<string, string>;
}): string => {
  if (
    row.fatigue.kind === "no-fatigue-row" ||
    row.fatigue.cause.kind === "no-cause"
  ) {
    return "—";
  }
  const { text } = row.fatigue.cause;
  return labels.get(text) ?? text;
};

const sortable = <Key extends string>({
  order,
  key,
}: {
  order: Order<Key>;
  key: Key;
}): FieldColumnSpec<FatigueRow>["sort"] => ({
  kind: "sortable",
  key,
  state: sortStateOf({ order, key }),
});

const fatigueColumn = ({
  id,
  label,
  width,
}: {
  id: string;
  label: string;
  width: ColumnState["width"];
}): Omit<ColumnState, "sort"> => ({
  id,
  label,
  info: { kind: "no-info" },
  width,
  pinned: "scrolls",
});

type FatigueColumnSpec = Omit<FieldColumnSpec<FatigueRow>, "kind" | "cell"> & {
  cell: (page: FatigueCellContext) => FieldColumnSpec<FatigueRow>["cell"];
};

export const withFatigueCells = ({
  columns,
  page,
}: {
  columns: readonly FatigueColumnSpec[];
  page: FatigueCellContext;
}): readonly FieldColumnSpec<FatigueRow>[] =>
  columns.map((spec) => ({ ...spec, kind: "field", cell: spec.cell(page) }));

export const toFatigueColumnStates = (
  columns: readonly FatigueColumnSpec[],
): readonly ColumnState[] =>
  columns.map((spec) => ({ ...spec.column, sort: spec.sort }));

export function fatigueColumns({
  view,
  order,
}: {
  view: AdViewMeta;
  order: Order<string>;
}): readonly FatigueColumnSpec[] {
  const { fatigue } = view.layouts;
  const ctr = metricOf(view, fatigue.ctr);
  const frequency = metricOf(view, fatigue.frequency);
  const spend = metricOf(view, fatigue.spend);
  const causeLabels = view.valueLabels.get(fatigue.cause) ?? new Map();
  const heading = (member: string) => memberLabel(view, member);
  return [
    {
      column: {
        ...fatigueColumn({ id: "creative", label: "Creative", width: "name" }),
        pinned: "pinned-left",
      },
      sort: { kind: "not-sortable" },
      cell: () => adNameCell,
    },
    {
      column: fatigueColumn({
        id: "campaign",
        label: heading(view.entities.campaign.name),
        width: "short-text",
      }),
      sort: { kind: "not-sortable" },
      cell:
        ({ campaigns }) =>
        (row) => {
          const campaign = campaigns.get(row.entityId);
          return campaign === undefined
            ? EMPTY_CELL
            : { kind: "text", text: campaign, tone: "secondary" };
        },
    },
    {
      column: fatigueColumn({ id: ctr.key, label: ctr.label, width: "metric" }),
      sort: sortable({ order, key: ctr.member }),
      cell: () => (row) => ({
        kind: "number",
        text: percentText({
          value: row.ctr,
          digits: 2,
        }),
      }),
    },
    {
      column: fatigueColumn({
        id: "peak",
        label: heading(fatigue.peakCtr),
        width: "metric",
      }),
      sort: sortable({ order, key: fatigue.peakCtr }),
      cell: () => (row) =>
        fatigueNumber({
          row,
          text: (fatigue) => percentText({ value: fatigue.peakCtr, digits: 2 }),
          tone: "secondary",
        }),
    },
    {
      column: fatigueColumn({
        id: "vs-peak",
        label: heading(fatigue.vsPeak),
        width: "metric",
      }),
      sort: sortable({ order, key: fatigue.vsPeak }),
      cell: () => (row) =>
        fatigueNumber({
          row,
          text: (fatigue) =>
            percentText({ value: fatigue.vsPeakPct, digits: 0 }),
          tone: "danger",
        }),
    },
    {
      column: fatigueColumn({
        id: "wow",
        label: heading(fatigue.wowCtrChange),
        width: "metric",
      }),
      sort: { kind: "not-sortable" },
      cell: () => (row) =>
        row.fatigue.kind === "no-fatigue-row"
          ? EMPTY_CELL
          : {
              kind: "text",
              text: wowText(row.fatigue.wowCtrChange),
              tone: wowTone(row.fatigue.wowCtrChange),
            },
    },
    {
      column: fatigueColumn({
        id: frequency.key,
        label: frequency.label,
        width: "metric",
      }),
      sort: sortable({ order, key: frequency.member }),
      cell: () => (row) => {
        const value = row.frequency;
        return {
          kind: "number",
          text: value === "not-reported" ? "—" : `${value.toFixed(2)}x`,
        };
      },
    },
    {
      column: fatigueColumn({
        id: "days",
        label: heading(fatigue.daysActive),
        width: "count",
      }),
      sort: { kind: "not-sortable" },
      cell: () => (row) =>
        fatigueNumber({
          row,
          text: (fatigue) => `${fatigue.daysActive}d`,
          tone: "secondary",
        }),
    },
    {
      column: {
        ...fatigueColumn({
          id: "impact",
          label: "Impact",
          width: "short-text",
        }),
        info: {
          kind: "info",
          text: "Relative spend: the ad's share of total spend in the selected period",
        },
      },
      sort: sortable({ order, key: spend.member }),
      cell:
        ({ totalSpend }) =>
        (row) =>
          impactCell({
            value: row.spend,
            totalSpend,
          }),
    },
    {
      column: fatigueColumn({
        id: "status",
        label: heading(fatigue.status),
        width: "short-text",
      }),
      sort: sortable({ order, key: fatigue.status }),
      cell: () => (row) => healthCell({ row, view }),
    },
    {
      column: fatigueColumn({
        id: "cause",
        label: heading(fatigue.cause),
        width: "short-text",
      }),
      sort: { kind: "not-sortable" },
      cell: () => (row) => ({
        kind: "text",
        text: causeText({ row, labels: causeLabels }),
        tone: "secondary",
      }),
    },
  ];
}

export const toReportedMetrics = ({
  metrics,
  rows,
}: {
  metrics: readonly MetricColumn[];
  rows: readonly Metered[];
}): readonly MetricColumn[] => {
  const measured = rows;
  const reported = new Set(
    measured.flatMap((row) =>
      row.metrics.flatMap((cell) =>
        cell.value === "not-reported" ? [] : [cell.column.key],
      ),
    ),
  );
  return measured.length === 0
    ? metrics
    : metrics.filter(
        (column) =>
          column.whenEmpty === "show-dash" || reported.has(column.key),
      );
};

export const toEntityCardMedia = ({
  media,
  choice,
}: {
  media: AdMedia;
  choice: CreativeChoice;
}): EntityCardMedia => {
  switch (media.kind) {
    case "media": {
      const { creatives } = media;
      const shown = shownCreativeOf({ creatives, choice });
      return creatives.length > 1
        ? {
            kind: "carousel",
            shown,
            index: creatives.indexOf(shown),
            count: creatives.length,
            steps: {
              1: steppedChoiceOf({ creatives, choice, step: 1 }),
              [-1]: steppedChoiceOf({ creatives, choice, step: -1 }),
            },
          }
        : { kind: "single", shown };
    }
    case "no-media": {
      return { kind: "no-media" };
    }
    default: {
      return media satisfies never;
    }
  }
};

export const shownChoiceOf = (media: EntityCardMedia): CreativeChoice => {
  switch (media.kind) {
    case "no-media": {
      return { kind: "first-creative" };
    }
    case "single":
    case "carousel": {
      return { kind: "picked", creativeId: media.shown.creativeId };
    }
    default: {
      return media satisfies never;
    }
  }
};
